import * as PIXI from 'pixi.js';
import { getRenderer } from '__editor/panelsEditor/components/renderer/renderer';
import { getStage, updateLayersOrder } from '__editor/panelsEditor/components/stage/stage';
import { dispatch, state } from '__common/store';
import { measurementToolModel } from '__editor/googleMapsRoofsSelector/models/measurementTool';
import { RESET_CURRENT_MEASUREMENT } from '__editor/googleMapsRoofsSelector/components/measurementTool/measurementToolActions';

let measurementContainer: PIXI.Container;
export const MEASUREMENT_LAYER_Z_INDEX = 99999;

function ensureMeasurementContainer(): PIXI.Container {
  if (!measurementContainer || measurementContainer.destroyed) {
    measurementContainer = new PIXI.Container();
    measurementContainer.sortableChildren = true;
  }

  // Keep measurements above panels, roof edges, and the fog overlay.
  measurementContainer.zIndex = MEASUREMENT_LAYER_Z_INDEX;
  measurementContainer.name = 'measurementToolLayer';
  measurementContainer.eventMode = 'none';
  measurementContainer.interactiveChildren = false;
  return measurementContainer;
}

// Replacing stage.children (panel, cursor, and zone redraws) drops this
// layer without clearing parent. parent === stage is then a lie, and the
// lines vanish while Redux still has them. addChild on a listed child moves
// it to the end so a later sibling at the same zIndex cannot cover it.
function attachMeasurementContainer(stage: PIXI.Container) {
  const container = ensureMeasurementContainer();
  const index = stage.children.indexOf(container);
  if (index === -1) {
    container.parent = null;
    stage.addChild(container);
    return;
  }
  if (index !== stage.children.length - 1) {
    stage.addChild(container);
  }
}

// Panel placement redraws zones, array numbers, and the cursor without
// changing the saved measurements. Reattach the existing graphics. Rebuilding
// them here destroyed the lines SM Ascender had just drawn.
export function raiseMeasurementLayer() {
  const stage = getStage();
  if (!stage || !measurementContainer || measurementContainer.destroyed || measurementContainer.children.length === 0) {
    return;
  }
  attachMeasurementContainer(stage);
}

export function drawingMeasurementToolOnStage() {
  const { measurementTool: { enabled, visible, startingPoint, endingPoint, measurements }, background: { metersPerPixel }, projectConfiguration : { productId, inputUnit } } = state();
  const stage = getStage();
  if (stage) {
    const container = ensureMeasurementContainer();
    attachMeasurementContainer(stage);

    const detached = container.removeChildren();
    for (let i = 0; i < detached.length; i += 1) {
      const child = detached[i];
      if (child && !child.destroyed) {
        child.destroy({ children: true });
      }
    }

    if (visible) {
      // Render saved measurements
      measurements.forEach((m, index) => {
        const mModel = measurementToolModel(m.startingPoint, m.endingPoint, stage.scale.x, productId, inputUnit, true, metersPerPixel, index);
        if (mModel) {
          mModel.zIndex = index;
          container.addChild(mModel);
        }
      });
    }

    // Render current measurement (the one being drawn)
    if (enabled && startingPoint && endingPoint) {
      const measurementPreview = measurementToolModel(startingPoint, endingPoint, stage.scale.x, productId, inputUnit, true, metersPerPixel);
      if (measurementPreview) {
        measurementPreview.zIndex = 9999;
        container.addChild(measurementPreview);
      }
    }

    const renderer = getRenderer();
    if (renderer) {
      stage.children = updateLayersOrder(stage.children);
      renderer.render(stage);
    }
  }
}

export const clearMeasurements = () => {
  const stage = getStage();

  if (stage && measurementContainer && !measurementContainer.destroyed) {
    measurementContainer.removeChildren();
    dispatch(RESET_CURRENT_MEASUREMENT());
    const renderer = getRenderer();
    if (renderer) renderer.render(stage);
  }
};
